import { EntityDefinition } from '@/types/crud';
import { TRANSFORMATION_STATUS_OPTIONS, DEFAULT_STATUS_VALUES } from '@/types/enums';

export const transformationEntity: EntityDefinition = {
  name: 'transformation',
  displayName: 'Transformations',
  displayNameSingular: 'Transformation',
  apiEndpoint: '/generic/transformations',
  idField: 'id',
  nameField: 'name',
  copyable: true,
  deletable: true,
  searchable: true,
  sortable: true,
  pageSize: 20,
  fields: [
    {
      key: 'name',
      label: 'Transformation Name',
      type: 'text',
      required: true,
      placeholder: 'Enter transformation name',
      validation: {
        minLength: 2,
        maxLength: 100
      }
    },
    {
      key: 'description',
      label: 'Description',
      type: 'textarea',
      required: false,
      placeholder: 'Enter transformation description',
      validation: {
        maxLength: 1000
      }
    },
    {
      key: 'hierarchy_type',
      label: 'Hierarchy Type',
      type: 'select',
      required: true,
      options: [
        { value: 'TRANSFORMATION', label: 'transformation.types.transformation' },
        { value: 'PROGRAM', label: 'transformation.types.program' },
        { value: 'PROJECT', label: 'transformation.types.project' },
        { value: 'SUB_PROJECT', label: 'transformation.types.subProject' }
      ],
      defaultValue: 'TRANSFORMATION'
    },
    {
      key: 'parent_id',
      label: 'Parent Transformation',
      type: 'select',
      required: false,
      placeholder: 'Select parent transformation',
      options: [] // Will be populated dynamically
    },
    {
      key: 'is_template',
      label: 'Is Template',
      type: 'checkbox',
      required: false,
      defaultValue: false
    },
    {
      key: 'template_id',
      label: 'Template',
      type: 'select',
      required: false,
      placeholder: 'Select template',
      options: [] // Will be populated dynamically
    },
    {
      key: 'industry',
      label: 'Industry',
      type: 'select',
      required: false,
      options: [
        { value: 'Cross-Industry', label: 'transformation.industries.crossIndustry' },
        { value: 'Manufacturing', label: 'transformation.industries.manufacturing' },
        { value: 'Healthcare', label: 'transformation.industries.healthcare' },
        { value: 'Financial Services', label: 'transformation.industries.financialServices' },
        { value: 'Retail', label: 'transformation.industries.retail' },
        { value: 'Technology', label: 'transformation.industries.technology' },
        { value: 'Energy', label: 'transformation.industries.energy' },
        { value: 'Transportation', label: 'transformation.industries.transportation' },
        { value: 'Education', label: 'transformation.industries.education' },
        { value: 'Government', label: 'transformation.industries.government' }
      ]
    },
    {
      key: 'functional_area',
      label: 'Functional Area',
      type: 'select',
      required: false,
      options: [
        { value: 'Strategy & Planning', label: 'transformation.functionalAreas.strategyPlanning' },
        { value: 'Operations', label: 'transformation.functionalAreas.operations' },
        { value: 'Finance', label: 'transformation.functionalAreas.finance' },
        { value: 'IT', label: 'transformation.functionalAreas.it' },
        { value: 'HR', label: 'transformation.functionalAreas.hr' },
        { value: 'Marketing', label: 'transformation.functionalAreas.marketing' },
        { value: 'Sales', label: 'transformation.functionalAreas.sales' },
        { value: 'Procurement', label: 'transformation.functionalAreas.procurement' },
        { value: 'Engineering', label: 'transformation.functionalAreas.engineering' },
        { value: 'Product Development', label: 'transformation.functionalAreas.productDevelopment' },
        { value: 'Compliance', label: 'transformation.functionalAreas.compliance' },
        { value: 'Sustainability', label: 'transformation.functionalAreas.sustainability' }
      ]
    },
    {
      key: 'transformation_type',
      label: 'Transformation Type',
      type: 'select',
      required: false,
      options: [
        { value: 'Digital Transformation', label: 'transformation.types.digitalTransformation' },
        { value: 'Process Transformation', label: 'transformation.types.processTransformation' },
        { value: 'Organizational Transformation', label: 'transformation.types.organizationalTransformation' },
        { value: 'Technology Transformation', label: 'transformation.types.technologyTransformation' },
        { value: 'Cultural Transformation', label: 'transformation.types.culturalTransformation' },
        { value: 'Assessment & Planning', label: 'transformation.types.assessmentPlanning' },
        { value: 'Business Case Development', label: 'transformation.types.businessCaseDevelopment' },
        { value: 'Platform Selection', label: 'transformation.types.platformSelection' },
        { value: 'Data Migration', label: 'transformation.types.dataMigration' },
        { value: 'Change Management', label: 'transformation.types.changeManagement' },
        { value: 'System Integration', label: 'transformation.types.systemIntegration' },
        { value: 'Collaboration Platform', label: 'transformation.types.collaborationPlatform' },
        { value: 'Process Automation', label: 'transformation.types.processAutomation' },
        { value: 'AI Integration', label: 'transformation.types.aiIntegration' },
        { value: 'Digital Twin', label: 'transformation.types.digitalTwin' },
        { value: 'Automation', label: 'transformation.types.automation' },
        { value: 'ESG Integration', label: 'transformation.types.esgIntegration' },
        { value: 'Circular Economy', label: 'transformation.types.circularEconomy' },
        { value: 'Customization Platform', label: 'transformation.types.customizationPlatform' }
      ]
    },
    {
      key: 'complexity_level',
      label: 'Complexity Level',
      type: 'select',
      required: false,
      options: [
        { value: 1, label: 'transformation.complexity.level1' },
        { value: 2, label: 'transformation.complexity.level2' },
        { value: 3, label: 'transformation.complexity.level3' },
        { value: 4, label: 'transformation.complexity.level4' },
        { value: 5, label: 'transformation.complexity.level5' }
      ]
    },
    {
      key: 'estimated_duration_months',
      label: 'Estimated Duration (Months)',
      type: 'number',
      required: false,
      placeholder: 'Enter estimated duration in months',
      validation: {
        min: 1,
        max: 120
      }
    },
    {
      key: 'estimated_cost_range_min',
      label: 'Estimated Cost Range (Min)',
      type: 'number',
      required: false,
      placeholder: 'Enter minimum cost estimate'
    },
    {
      key: 'estimated_cost_range_max',
      label: 'Estimated Cost Range (Max)',
      type: 'number',
      required: false,
      placeholder: 'Enter maximum cost estimate'
    },
    {
      key: 'tags',
      label: 'Tags',
      type: 'text',
      required: false,
      placeholder: 'Enter tags separated by commas'
    },
    {
      key: 'objectives',
      label: 'Objectives',
      type: 'textarea',
      required: false,
      placeholder: 'Enter objectives as JSON array'
    },
    {
      key: 'expected_outcomes',
      label: 'Expected Outcomes',
      type: 'textarea',
      required: false,
      placeholder: 'Enter expected outcomes as JSON array'
    },
    {
      key: 'cost_drivers',
      label: 'Cost Drivers',
      type: 'textarea',
      required: false,
      placeholder: 'Enter cost drivers as JSON object'
    },
    {
      key: 'cost_calculation_formula',
      label: 'Cost Calculation Formula',
      type: 'text',
      required: false,
      placeholder: 'Enter cost calculation formula'
    },
    {
      key: 'custom_assumptions',
      label: 'Custom Assumptions',
      type: 'textarea',
      required: false,
      placeholder: 'Enter custom assumptions as JSON object'
    },
    {
      key: 'calculated_cost',
      label: 'Calculated Cost',
      type: 'number',
      required: false,
      readonly: true
    },
    {
      key: 'organizational_hierarchy_id',
      label: 'Organizational Hierarchy',
      type: 'select',
      required: false,
      placeholder: 'Select organizational hierarchy',
      options: [] // Will be populated dynamically
    },
    {
      key: 'functional_hierarchy_id',
      label: 'Functional Hierarchy',
      type: 'select',
      required: false,
      placeholder: 'Select functional hierarchy',
      options: [] // Will be populated dynamically
    },
    {
      key: 'geographic_hierarchy_id',
      label: 'Geographic Hierarchy',
      type: 'select',
      required: false,
      placeholder: 'Select geographic hierarchy',
      options: [] // Will be populated dynamically
    },
    {
      key: 'product_hierarchy_id',
      label: 'Product Hierarchy',
      type: 'select',
      required: false,
      placeholder: 'Select product hierarchy',
      options: [] // Will be populated dynamically
    },
    {
      key: 'marketplace_rating',
      label: 'Marketplace Rating',
      type: 'number',
      required: false,
      readonly: true,
      validation: {
        min: 0,
        max: 5
      }
    },
    {
      key: 'marketplace_reviews_count',
      label: 'Marketplace Reviews Count',
      type: 'number',
      required: false,
      readonly: true
    },
    {
      key: 'published_at',
      label: 'Published At',
      type: 'datetime',
      required: false,
      readonly: true
    },
    {
      key: 'conversion_status',
      label: 'Conversion Status',
      type: 'select',
      required: false,
      options: [
        { value: 'NOT_CONVERTED', label: 'transformation.conversionStatus.notConverted' },
        { value: 'CONVERTED', label: 'transformation.conversionStatus.converted' },
        { value: 'PARTIALLY_CONVERTED', label: 'transformation.conversionStatus.partiallyConverted' }
      ],
      defaultValue: 'NOT_CONVERTED'
    },
    {
      key: 'is_converted_to_project',
      label: 'Is Converted to Project',
      type: 'checkbox',
      required: false,
      defaultValue: false
    },
    {
      key: 'converted_at',
      label: 'Converted At',
      type: 'datetime',
      required: false,
      readonly: true
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      options: TRANSFORMATION_STATUS_OPTIONS,
      defaultValue: DEFAULT_STATUS_VALUES.TRANSFORMATION
    },
    {
      key: 'classification',
      label: 'Classification',
      type: 'select',
      required: false,
      options: [
        { value: 'Public', label: 'Public' },
        { value: 'Internal', label: 'Internal' },
        { value: 'Confidential', label: 'Confidential' },
        { value: 'Secret', label: 'Secret' },
        { value: 'Top Secret', label: 'Top Secret' }
      ],
      defaultValue: 'Internal',
      placeholder: 'Classification level (default: Internal)'
    },
    {
      key: 'start_date',
      label: 'Start Date',
      type: 'date',
      required: false,
      placeholder: 'Select start date'
    },
    {
      key: 'end_date',
      label: 'End Date',
      type: 'date',
      required: false,
      placeholder: 'Select end date'
    },
    {
      key: 'budget',
      label: 'Budget',
      type: 'number',
      required: false,
      placeholder: 'Enter budget amount'
    }
  ],
  tableColumns: [
    { key: 'name', label: 'Name', sortable: true, width: '20%' },
    { key: 'hierarchy_type', label: 'Type', sortable: true, width: '6%' },
    { key: 'complexity_level', label: 'Complexity', sortable: true, width: '6%' },
    { key: 'estimated_duration_months', label: 'Duration', sortable: true, width: '6%' },
    { key: 'estimated_cost_range_min', label: 'Cost', sortable: true, width: '8%' },
    { key: 'status', label: 'Status', sortable: true, width: '6%' },
    { 
      key: 'total_inchstones', 
      label: 'Total Inchstones', 
      sortable: false, 
      width: '8%',
      render: (_value: any, row: any) => {
        const stats = row.inchstone_stats;
        return stats ? stats.total_inchstones : 0;
      }
    },
    { 
      key: 'completed_inchstones', 
      label: 'Completed', 
      sortable: false, 
      width: '8%',
      render: (_value: any, row: any) => {
        const stats = row.inchstone_stats;
        return stats ? stats.completed_inchstones : 0;
      }
    },
    { 
      key: 'delayed_inchstones', 
      label: 'Delayed', 
      sortable: false, 
      width: '8%',
      render: (_value: any, row: any) => {
        const stats = row.inchstone_stats;
        return stats ? stats.delayed_inchstones : 0;
      }
    },
    { 
      key: 'created_at', 
      label: 'Created', 
      sortable: true, 
      width: '8%',
      render: (value: string) => {
        if (!value) return '';
        const date = new Date(value);
        return date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        });
      }
    }
  ],
  searchFields: ['name', 'description', 'industry', 'functional_area', 'transformation_type'],
  defaultSort: { field: 'name', direction: 'asc' }
}; 